<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ i18n.t('pages.hash.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="4" y1="9" x2="20" y2="9"/>
            <line x1="4" y1="15" x2="20" y2="15"/>
            <line x1="10" y1="3" x2="8" y2="21"/>
            <line x1="16" y1="3" x2="14" y2="21"/>
          </svg>
        </div>
        <h1 class="page-title">{{ i18n.t('pages.hash.title') }}</h1>
        <p class="page-subtitle">{{ i18n.t('pages.hash.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="hash-card">
        <div class="input-section">
          <label class="input-label">{{ i18n.locale === 'zh' ? '输入文本' : 'Input Text' }}</label>
          <textarea
            v-model="inputText"
            :placeholder="i18n.locale === 'zh' ? '输入要哈希的文本...' : 'Enter text to hash...'"
            class="hash-textarea"
            rows="4"
          ></textarea>
        </div>

        <div class="options-section">
          <label class="option-label">{{ i18n.locale === 'zh' ? '哈希算法' : 'Algorithm' }}</label>
          <div class="algorithm-grid">
            <button
              v-for="algo in algorithms"
              :key="algo"
              @click="selectedAlgorithm = algo"
              :class="['algo-btn', { active: selectedAlgorithm === algo }]"
            >
              {{ algo }}
            </button>
          </div>
        </div>

        <div class="action-row">
          <button @click="generateHash" class="generate-btn" :disabled="!inputText.trim()">
            {{ i18n.t('pages.hash.generate') }}
          </button>
          <button @click="copyHash" class="copy-btn" :disabled="!result">
            {{ i18n.locale === 'zh' ? '复制' : 'Copy' }}
          </button>
        </div>

        <div v-if="result" class="result-section">
          <label class="result-label">{{ i18n.locale === 'zh' ? '哈希结果' : 'Hash Result' }}</label>
          <div class="result-box">{{ result }}</div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'HashGeneratorPage',
  inject: ['i18n'],
  data() {
    return {
      inputText: '',
      selectedAlgorithm: 'SHA-256',
      algorithms: ['MD5', 'SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'],
      result: ''
    }
  },
  methods: {
    async generateHash() {
      if (!this.inputText.trim()) {
        return
      }

      const encoder = new TextEncoder()
      const data = encoder.encode(this.inputText)
      let hashBuffer

      switch (this.selectedAlgorithm) {
        case 'MD5':
          hashBuffer = await crypto.subtle.digest('MD5', data)
          break
        case 'SHA-1':
          hashBuffer = await crypto.subtle.digest('SHA-1', data)
          break
        case 'SHA-256':
          hashBuffer = await crypto.subtle.digest('SHA-256', data)
          break
        case 'SHA-384':
          hashBuffer = await crypto.subtle.digest('SHA-384', data)
          break
        case 'SHA-512':
          hashBuffer = await crypto.subtle.digest('SHA-512', data)
          break
        default:
          return
      }

      const hashArray = Array.from(new Uint8Array(hashBuffer))
      this.result = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    },
    async copyHash() {
      if (!this.result) {
        return
      }
      try {
        await navigator.clipboard.writeText(this.result)
      } catch (e) {
        console.error('Copy failed', e)
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 240, 255, 0.1) 0%, transparent 70%);
  top: 50%;
  right: -200px;
  transform: translateY(-50%);
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 700px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover { color: var(--accent-primary); }
.breadcrumb-sep { color: var(--border-color); }
.breadcrumb-current { color: var(--accent-success); }

.header-content { text-align: center; }

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-success);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 300;
}

.page-content {
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.hash-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.input-section { margin-bottom: 28px; }

.input-label, .result-label, .option-label {
  display: block;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.hash-textarea {
  width: 100%;
  padding: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.3s;
}

.hash-textarea:focus {
  outline: none;
  border-color: var(--accent-success);
}

.hash-textarea::placeholder { color: var(--text-secondary); }

.options-section { margin-bottom: 28px; }

.algorithm-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.algo-btn {
  padding: 10px 18px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.algo-btn:hover { border-color: var(--accent-success); color: var(--accent-success); }
.algo-btn.active {
  background: var(--accent-success);
  color: var(--bg-primary);
  border-color: var(--accent-success);
}

.action-row {
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}

.generate-btn, .copy-btn {
  padding: 14px 28px;
  border-radius: 10px;
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.generate-btn {
  background: var(--accent-primary);
  color: var(--bg-primary);
  border: none;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(232, 255, 0, 0.3);
}

.generate-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.copy-btn {
  background: transparent;
  color: var(--accent-success);
  border: 1px solid var(--accent-success);
}

.copy-btn:hover:not(:disabled) {
  background: var(--accent-success);
  color: var(--bg-primary);
}

.copy-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.result-section { margin-top: 24px; }

.result-box {
  padding: 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--accent-success);
  font-family: var(--font-mono);
  font-size: 14px;
  word-break: break-all;
  line-height: 1.6;
}

@media (max-width: 640px) {
  .page-container { padding: 24px 16px 40px; }
  .page-title { font-size: 36px; }
  .hash-card { padding: 24px; }
  .algorithm-grid { gap: 8px; }
  .algo-btn { padding: 8px 14px; font-size: 12px; }
  .action-row { flex-direction: column; }
}
</style>
